import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Currency, CurrencyCode } from "@/types";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "stagestudios:currency";

/** Format-money option bag — kept as a discrete interface so call sites
 *  can rely on autocomplete (showCode, decimals, signed, currency). */
export interface FormatMoneyOptions {
  /** Override the active currency for this single call. */
  currency?: CurrencyCode;
  /** Force a specific fractional-digit count (e.g. 3 for per-credit). */
  decimals?: number;
  /** Always prefix with +/-, used in P&L rows. */
  signed?: boolean;
}

export interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (next: CurrencyCode) => void;
  rates: Record<string, number>;
  currencies: Currency[];
  loading: boolean;
  convert: (usdAmount: number | null | undefined, targetCurrency?: CurrencyCode) => number;
  formatMoney: (usd: number | null | undefined, opts?: FormatMoneyOptions) => string;
  meta: Currency;
  updatedAt: string | null;
  source: string;
}

// Hardcoded sensible defaults so the UI renders meaningful numbers even
// before the /api/currency/rates response arrives (or if it fails entirely).
const DEFAULT_RATES: Record<string, number> = { USD: 1.0, AED: 3.6725, GBP: 0.79, EUR: 0.92 };
const DEFAULT_META: Currency[] = [
  { code: "USD", symbol: "$",   label: "US Dollar",     locale: "en-US", decimals: 2, rate: 1.0     },
  { code: "AED", symbol: "AED", label: "UAE Dirham",    locale: "en-AE", decimals: 2, rate: 3.6725  },
  { code: "GBP", symbol: "£",   label: "British Pound", locale: "en-GB", decimals: 2, rate: 0.79    },
  { code: "EUR", symbol: "€",   label: "Euro",          locale: "en-IE", decimals: 2, rate: 0.92    },
];

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: "USD",
  setCurrency: () => {},
  rates: DEFAULT_RATES,
  currencies: DEFAULT_META,
  loading: false,
  convert: (usd) => Number(usd || 0),
  formatMoney: (usd) => `$${Number(usd || 0).toFixed(2)}`,
  meta: DEFAULT_META[0],
  updatedAt: null,
  source: "fallback",
});

const SUPPORTED_CODES: CurrencyCode[] = ["USD", "AED", "GBP", "EUR"];
const isCurrencyCode = (v: unknown): v is CurrencyCode =>
  typeof v === "string" && (SUPPORTED_CODES as string[]).includes(v);

const readStored = (): CurrencyCode => {
  if (typeof window === "undefined") return "USD";
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    if (isCurrencyCode(v)) return v;
  } catch (_) { /* fall through to autodetect */ }
  // iter49 P2 #4 product: auto-detect a sensible default from the browser
  // locale. Stage Studios is UAE/UK/London-focused, so we map UAE+GCC
  // locales to AED, UK locales to GBP, EU locales to EUR; everything else
  // falls back to USD. This is a FIRST-VISIT-ONLY default — the user can
  // always switch via the currency picker, and their choice persists in
  // localStorage.
  try {
    const langs = (navigator.languages || [navigator.language || "en-US"]).map((s) => (s || "").toLowerCase());
    for (const lang of langs) {
      // Gulf states + UAE → AED (we don't yet support SAR/KWD/etc; AED is
      // the closest the user-facing list offers and it's the operator's
      // primary market).
      if (lang.includes("ae") || lang.includes("sa") || lang.includes("ar-")) return "AED";
      if (lang.startsWith("en-gb") || lang === "en-uk") return "GBP";
      if (
        lang.startsWith("de") || lang.startsWith("fr") || lang.startsWith("es") ||
        lang.startsWith("it") || lang.startsWith("nl") || lang.startsWith("pt") ||
        lang.startsWith("pl") || lang.startsWith("fi") || lang.startsWith("sv") ||
        lang.startsWith("da") || lang.startsWith("no") || lang.startsWith("el")
      ) return "EUR";
    }
  } catch (_) { /* navigator missing — default to USD */ }
  return "USD";
};

interface CurrencyRatesResponse {
  rates?: Record<string, number>;
  currencies?: Currency[];
  updated_at?: string | null;
  source?: string;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(readStored);
  const [rates, setRates] = useState<Record<string, number>>(DEFAULT_RATES);
  const [currencies, setCurrencies] = useState<Currency[]>(DEFAULT_META);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [source, setSource] = useState<string>("fallback");

  // Pull live rates once on mount. exchangerate provider data is cached
  // 24h on the backend, so this single call also tells us when the
  // last refresh happened.
  useEffect(() => {
    let cancelled = false;
    const fetchRates = () => {
      setLoading(true);
      fetch(`${API}/api/currency/rates`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data: CurrencyRatesResponse) => {
          if (cancelled) return;
          if (data?.rates) setRates({ ...DEFAULT_RATES, ...data.rates });
          if (Array.isArray(data?.currencies) && data.currencies.length > 0) {
            setCurrencies(data.currencies);
          }
          setUpdatedAt(data?.updated_at || null);
          setSource(data?.source || "fallback");
        })
        .catch(() => { /* defaults already loaded */ })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    fetchRates();

    // iter49 P3 #14: refresh rates when the tab regains focus after being
    // backgrounded for > 24h. The backend caches FX rates for 24h, so
    // long-lived sessions (overnight, open-laptop weekends) would see
    // stale rates without this. Cheap — typical browser tab visibility
    // changes a few times a day, and the backend already has the cache.
    let lastFetch = Date.now();
    const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;
    const onVisibility = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetch < REFRESH_AFTER_MS) return;
      lastFetch = Date.now();
      fetchRates();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const setCurrency = useCallback((next: CurrencyCode) => {
    if (!isCurrencyCode(next)) return;
    setCurrencyState(next);
    try { window.localStorage?.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
  }, []);

  const convert = useCallback<CurrencyContextValue["convert"]>((usdAmount, targetCurrency) => {
    const target = targetCurrency || currency;
    const rate = rates[target] ?? DEFAULT_RATES[target] ?? 1.0;
    return Number(usdAmount || 0) * rate;
  }, [currency, rates]);

  const meta = useMemo<Currency>(
    () => currencies.find((c) => c.code === currency) || DEFAULT_META.find((c) => c.code === currency) || DEFAULT_META[0],
    [currencies, currency],
  );

  /**
   * Format a USD amount in the active currency. Options:
   *  - showCode: false (default) — show symbol only ($1.99 / AED 7.31 / £1.51 / €1.75)
   *  - decimals: meta.decimals (2) — fractional digits
   *  - signed:   false — prefix +/- explicitly (used in P&L rows)
   *  - currency: override the active currency for a single call
   */
  const formatMoney = useCallback<CurrencyContextValue["formatMoney"]>((usd, opts = {}) => {
    const target = opts.currency || currency;
    const m = currencies.find((c) => c.code === target) || DEFAULT_META.find((c) => c.code === target) || DEFAULT_META[0];
    const rate = rates[target] ?? DEFAULT_RATES[target] ?? 1.0;
    const raw = Number(usd || 0) * rate;
    const decimals = opts.decimals ?? m.decimals ?? 2;
    const abs = Math.abs(raw).toLocaleString(m.locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    // AED uses the 3-letter code prefix because most UAE pricing UX shows
    // "AED 1,234.56" rather than a ج.إ glyph. Others get their symbol.
    const sym = m.symbol;
    const isCodePrefix = sym.length >= 2; // "AED" -> code prefix; "$" "£" "€" -> glyph prefix
    const head = isCodePrefix ? `${sym} ` : sym;
    const sign = opts.signed ? (raw < 0 ? "-" : "+") : (raw < 0 ? "-" : "");
    return `${sign}${head}${abs}`;
  }, [currency, currencies, rates]);

  const value = useMemo<CurrencyContextValue>(() => ({
    currency,
    setCurrency,
    rates,
    currencies,
    loading,
    convert,
    formatMoney,
    meta,
    updatedAt,
    source,
  }), [currency, setCurrency, rates, currencies, loading, convert, formatMoney, meta, updatedAt, source]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}
